import type {
  Reducer,
  ReducersMapObject,
  StateFromReducersMapObject,
} from '@reduxjs/toolkit';
import {combineReducers, configureStore} from '@reduxjs/toolkit';
import {IS_DEV} from '@src/config.ts';

// Overload for when reducers is a ReducersMapObject
function initStore<M extends ReducersMapObject>(
  reducers: M,
  preloadedState?: Partial<StateFromReducersMapObject<M>>,
): ReturnType<typeof configureStore<StateFromReducersMapObject<M>>>;

// Overload for when reducers is a single Reducer
function initStore<S>(
  reducers: Reducer<S>,
  preloadedState?: Partial<S>,
): ReturnType<typeof configureStore<S>>;

// Implementation
function initStore<S>(
  reducers: Reducer<S> | ReducersMapObject<S>,
  preloadedState?: Partial<S>,
) {
  const reducer =
    typeof reducers === 'object' ? combineReducers(reducers) : reducers;

  return configureStore({
    reducer: reducer as Reducer<S>,
    preloadedState: preloadedState as S | undefined,
    middleware: getDefaultMiddleware =>
      getDefaultMiddleware({
        // WARNING! The checks here are valuable but they have a
        // significant impact on performance.
        serializableCheck: false,
        immutableCheck: false,
      }),
    devTools:
      IS_DEV || window.location.hostname === 'static.prod.planet-labs.com',
  });
}

export default initStore;
